import React, { useRef, useState, useCallback, useEffect } from 'react';
import styles from './DynamicColorSidebar.module.css';

type DynamicColorSidebarProps = {
  children: React.ReactNode;
  className?: string;
  disableColorChange?: boolean;
};

const DynamicColorSidebar: React.FC<DynamicColorSidebarProps> = ({ children, className = '', disableColorChange = false }) => {
  const [textColor, setTextColor] = useState<'white' | 'black'>('white');
  const sidebarRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastValidColorRef = useRef<'white' | 'black'>('white');

  // Forza il colore bianco quando disableColorChange è true
  useEffect(() => {
    if (disableColorChange) {
      setTextColor('white');
      lastValidColorRef.current = 'white';
    }
  }, [disableColorChange]);

  const getPixelBrightness = useCallback((img: HTMLImageElement, x: number, y: number): number => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return 0.5;

    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    
    ctx.drawImage(img, 0, 0);
    
    const imageData = ctx.getImageData(x, y, 1, 1);
    const r = imageData.data[0];
    const g = imageData.data[1];
    const b = imageData.data[2];
    
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  }, []);

  const getElementBrightness = useCallback((element: Element): number => {
    const computedStyle = window.getComputedStyle(element);
    const backgroundColor = computedStyle.backgroundColor;
    
    // Se è trasparente, controlla l'elemento padre
    if (backgroundColor === 'rgba(0, 0, 0, 0)' || backgroundColor === 'transparent') {
      const parent = element.parentElement;
      if (parent) {
        return getElementBrightness(parent);
      }
      return 0.5; // Default
    }
    
    // Estrai i valori RGB
    const rgbMatch = backgroundColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (rgbMatch) {
      const r = parseInt(rgbMatch[1]);
      const g = parseInt(rgbMatch[2]);
      const b = parseInt(rgbMatch[3]);
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    }
    
    return 0.5; // Default
  }, []);

  const updateTextColor = useCallback(() => {
    if (!sidebarRef.current || disableColorChange) return;

    // Su mobile, mantieni sempre il colore bianco e non cambiare
    if (window.innerWidth <= 768) {
      setTextColor('white');
      return;
    }

    // Debounce per evitare troppi aggiornamenti
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      if (!sidebarRef.current) return;

      const sidebarRect = sidebarRef.current.getBoundingClientRect();
      const sidebarCenterX = sidebarRect.left + sidebarRect.width / 2;
      const sidebarCenterY = sidebarRect.top + sidebarRect.height / 2;

      // Prova prima con elementsFromPoint per avere tutti gli elementi
      const elementsAtPoint = document.elementsFromPoint(sidebarCenterX, sidebarCenterY);
      
      // Trova il primo elemento valido (non sidebar)
      let validElement = elementsAtPoint.find(el => 
        el !== sidebarRef.current && 
        !el.classList.contains('dynamicSidebar') &&
        el.tagName !== 'H1' && 
        el.tagName !== 'NAV' &&
        el.tagName !== 'BUTTON'
      );
      
      // Se siamo in una pagina progetto e non troviamo elementi validi,
      // cerca direttamente le immagini nel masonry
      if (!validElement || validElement.tagName === 'DIV') {
        const masonryImages = document.querySelectorAll('.masonry-item-img');
        if (masonryImages.length > 0) {
          // Trova l'immagine più vicina al centro della sidebar
          let closestImage: Element | null = null;
          let minDistance = Infinity;
          
          masonryImages.forEach(img => {
            const rect = img.getBoundingClientRect();
            const imgCenterX = rect.left + rect.width / 2;
            const imgCenterY = rect.top + rect.height / 2;
            const distance = Math.sqrt(
              Math.pow(sidebarCenterX - imgCenterX, 2) + 
              Math.pow(sidebarCenterY - imgCenterY, 2)
            );
            
            if (distance < minDistance) {
              minDistance = distance;
              closestImage = img;
            }
          });
          
          if (closestImage) {
            validElement = closestImage;
          }
        }
      }
      
      
      if (validElement) {
        // Se è un'immagine, usa l'analisi pixel
        if (validElement.tagName === 'IMG') {
          const img = validElement as HTMLImageElement;
          const imgRect = img.getBoundingClientRect();
          
          const imgX = Math.floor(((sidebarCenterX - imgRect.left) / imgRect.width) * img.naturalWidth);
          const imgY = Math.floor(((sidebarCenterY - imgRect.top) / imgRect.height) * img.naturalHeight);
          
          if (imgX >= 0 && imgY >= 0 && imgX < img.naturalWidth && imgY < img.naturalHeight) {
            const brightness = getPixelBrightness(img, imgX, imgY);
            const newColor = brightness > 0.5 ? 'black' : 'white';
            setTextColor(newColor);
            lastValidColorRef.current = newColor;
            return;
          }
        }
        
        // Altrimenti usa il colore di sfondo
        const brightness = getElementBrightness(validElement);
        const newColor = brightness > 0.5 ? 'black' : 'white';
        setTextColor(newColor);
        lastValidColorRef.current = newColor;
      } else {
        // Usa l'ultimo colore valido quando non trova elementi
        setTextColor(lastValidColorRef.current);
      }
    }, 50); // Debounce di 50ms
  }, [getPixelBrightness, getElementBrightness, disableColorChange]);

  useEffect(() => {
    const handleScroll = () => {
      if (disableColorChange) {
        setTextColor('white');
        return;
      }
      
      const scrollTop = window.pageYOffset;
      
      // Se siamo in cima alla pagina, forza il reset del colore
      if (scrollTop === 0) {
        // Reset del colore quando si torna in cima
        setTextColor('white');
        lastValidColorRef.current = 'white';
      }
      
      updateTextColor();
    };

    const handleResize = () => {
      if (disableColorChange) {
        setTextColor('white');
        return;
      }
      
      // Su mobile, forza il colore bianco
      if (window.innerWidth <= 768) {
        setTextColor('white');
      } else {
        updateTextColor();
      }
    };

    // Aggiorna il colore quando le immagini sono caricate
    const images = document.querySelectorAll('img');
    images.forEach(img => {
      if (img.complete) {
        updateTextColor();
      } else {
        img.addEventListener('load', updateTextColor);
      }
    });

    // Aggiorna inizialmente con un delay per permettere al DOM di stabilizzarsi
    setTimeout(() => {
      if (disableColorChange || window.innerWidth <= 768) {
        setTextColor('white');
      } else {
        updateTextColor();
      }
    }, 100);

    // Aggiorna anche dopo un delay più lungo per i cambi di vista
    setTimeout(() => {
      if (disableColorChange || window.innerWidth <= 768) {
        setTextColor('white');
      } else {
        updateTextColor();
      }
    }, 500);

    // Aggiorna quando il DOM cambia (per i cambi di vista)
    const observer = new MutationObserver(() => {
      setTimeout(() => {
        if (disableColorChange || window.innerWidth <= 768) {
          setTextColor('white');
        } else {
          updateTextColor();
        }
      }, 200);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      images.forEach(img => {
        img.removeEventListener('load', updateTextColor);
      });
      observer.disconnect();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [updateTextColor, disableColorChange]);

  return (
    <div 
      ref={sidebarRef} 
      className={`${styles.dynamicSidebar} ${className}`}
      style={{ color: textColor }}
    >
      {children}
    </div>
  );
};

export default DynamicColorSidebar;
